import type { InsightContent } from "./types";

export const hispanicMarketSeo: InsightContent = {
  id: "hispanic-market-seo",
  format: "guide",
  cluster: "Hispanic Search",
  moneyPage: {
    es: "/servicios/seo-bilingue-mercado-hispano",
    en: "/en/services/hispanic-market-seo",
  },
  path: {
    es: "/biblioteca/seo-mercado-hispano-estados-unidos",
    en: "/en/library/seo-us-hispanic-market",
  },
  published: "2026-08-11",
  updated: "2026-08-11",
  readingMinutes: 10,
  es: {
    title: "SEO para el mercado hispano de EE. UU.: más que traducir",
    h1: "SEO para el mercado hispano de Estados Unidos: por qué traducir no basta",
    description:
      "El mercado hispano de EE. UU. busca en inglés, en español y mezclando ambos. Cómo abordar keyword research, arquitectura y confianza sin adivinar cifras.",
    eyebrow: "Búsqueda Hispana",
    summary:
      "Llegar al usuario hispano en Estados Unidos exige entender cómo alterna entre idiomas al buscar, qué variación regional del español usa y qué señales de confianza espera, no simplemente traducir el sitio en inglés.",
    body: [
      {
        type: "p",
        text: "Uno de los errores más frecuentes al pensar en 'el mercado hispano de Estados Unidos' es tratarlo como un bloque que busca todo en español. La realidad de búsqueda es más mixta y más específica que eso.",
      },
      {
        type: "h2",
        text: "Code-switching y búsqueda en idioma mixto",
      },
      {
        type: "p",
        text: "Un mismo usuario bilingüe puede buscar un tema en inglés y otro relacionado en español, según el contexto, el nivel de familiaridad con el tema, o incluso el dispositivo que esté usando en ese momento. Ciertas categorías —salud, finanzas, temas familiares o de confianza— tienden a inclinarse hacia el idioma con el que la persona se siente emocionalmente más cómoda, incluso si domina el inglés perfectamente. Otras categorías incorporan términos en inglés dentro de una búsqueda mayoritariamente en español, porque así es como el sector los nombra en la práctica cotidiana.",
      },
      {
        type: "callout",
        text: "El comportamiento bilingüe no es una preferencia fija de idioma. Es un patrón que cambia según el tema, el momento y el contexto de cada búsqueda.",
      },
      {
        type: "h2",
        text: "El español de EE. UU. no es un solo español",
      },
      {
        type: "p",
        text: "El vocabulario de búsqueda varía según la región de origen predominante en cada zona del país. Una comunidad con fuerte presencia mexicana no necesariamente usa los mismos términos que una comunidad con fuerte presencia caribeña o centroamericana. Asumir un único glosario de palabras clave en español para todo el país ignora esa variación real.",
      },
      {
        type: "h2",
        text: "Señales de confianza para el comprador hispanohablante",
      },
      {
        type: "list",
        items: [
          "Contenido en español con la misma profundidad editorial que la versión en inglés, no una versión resumida o de menor prioridad.",
          "Atención al cliente y proceso de compra disponibles en el idioma en el que se hizo la búsqueda inicial.",
          "Prueba social y testimonios que reflejen a la audiencia real a la que se dirige el contenido.",
          "Consistencia entre lo que promete la página en español y lo que efectivamente se puede resolver en ese idioma más adelante en el proceso.",
        ],
      },
      {
        type: "h2",
        text: "Arquitectura bilingüe del sitio: opciones",
      },
      {
        type: "table",
        head: ["Opción", "Cuándo conviene", "Riesgo principal"],
        rows: [
          [
            "Subcarpetas por idioma (/es/, sin prefijo para inglés)",
            "Sitio con recursos limitados que quiere concentrar autoridad de dominio en un solo lugar",
            "Requiere hreflang y canonicals cuidadosos para evitar contenido duplicado",
          ],
          [
            "Subdominio en español (es.sitio.com)",
            "Equipos que gestionan el contenido en español como unidad más independiente",
            "Puede fragmentar autoridad si no se enlaza bien con la versión principal",
          ],
          [
            "Selector de idioma sin URLs separadas",
            "Rara vez recomendable para SEO",
            "Los motores de búsqueda no pueden indexar ni diferenciar versiones si el contenido cambia por JavaScript sin URLs propias",
          ],
        ],
      },
      {
        type: "h2",
        text: "Sobre los datos: qué no hacer",
      },
      {
        type: "p",
        text: "Es tentador citar cifras de volumen de búsqueda o tamaño de audiencia para justificar una estrategia bilingüe. El problema es que ese volumen depende de la categoría, la región y la herramienta usada, y varía constantemente. Cualquier decisión de qué palabras clave priorizar en español debe basarse en investigación de volumen y competencia hecha directamente sobre tu categoría y tu mercado, en el momento del proyecto — no en una cifra genérica citada sin fuente. Cuando ese dato específico todavía no existe para tu caso, lo correcto es señalarlo como pendiente de investigación, no rellenarlo con una cifra inventada.",
      },
      {
        type: "h2",
        text: "Localización vs. traducción: checklist",
      },
      {
        type: "steps",
        items: [
          "Investigar el comportamiento de búsqueda inglés/español específico de tu categoría, no asumirlo.",
          "Identificar qué consultas se benefician de vocabulario regional específico dentro de EE. UU.",
          "Verificar volumen y competencia reales por palabra clave en español para tu mercado, sin apoyarte en cifras genéricas.",
          "Diseñar arquitectura EN/ES con hreflang recíproco y canonicals correctos entre ambas versiones.",
          "Dar a la versión en español la misma profundidad editorial que a la versión en inglés.",
          "Habilitar atención al cliente y proceso de conversión en el idioma en que llegó el usuario.",
          "Medir el cruce entre idiomas: usuarios que investigan en un idioma y convierten en otro.",
        ],
      },
      {
        type: "h2",
        text: "El punto central",
      },
      {
        type: "p",
        text: "Tratar el español como una traducción secundaria del sitio en inglés desaprovecha una audiencia que busca, decide y confía de forma distinta según el idioma y el contexto. La estrategia empieza por entender ese comportamiento, no por traducir texto.",
      },
    ],
    cta: {
      text: "Si tu marca ya sirve o quiere servir al consumidor hispano en EE. UU., vale la pena revisar si tu estrategia actual refleja ese comportamiento bilingüe real.",
      label: "Ver SEO Bilingüe para el Mercado Hispano",
      href: "/servicios/seo-bilingue-mercado-hispano",
    },
    related: [
      {
        label: "Traducir palabras clave no es SEO internacional",
        href: "/biblioteca/traducir-keywords-no-es-seo-internacional",
      },
      { label: "SEO Internacional", href: "/servicios/seo-internacional" },
      {
        label: "SEO bilingüe para el mercado hispano",
        href: "/servicios/seo-bilingue-mercado-hispano",
      },
    ],
  },
  en: {
    title: "SEO for the US Hispanic Market: Beyond Translation",
    h1: "SEO for the US Hispanic Market: Why Translation Is Not Enough",
    description:
      "The US Hispanic market searches in English, Spanish, and mixed language. How to approach keyword research, architecture and trust without guessing at numbers.",
    eyebrow: "Hispanic Search",
    summary:
      "Reaching Hispanic users in the US requires understanding how they switch between languages while searching, which regional Spanish they use, and what trust signals they expect — not simply translating the English site.",
    body: [
      {
        type: "p",
        text: "One of the most common mistakes when thinking about 'the US Hispanic market' is treating it as a single block that searches everything in Spanish. The real search behavior is more mixed and more specific than that.",
      },
      {
        type: "h2",
        text: "Code-switching and mixed-language querying",
      },
      {
        type: "p",
        text: "The same bilingual user might search one topic in English and a related one in Spanish, depending on context, familiarity with the subject, or even the device they're using at that moment. Certain categories — health, finance, family matters, or anything trust-sensitive — tend to skew toward the language the person feels emotionally closer to, even if they're fully fluent in English. Other categories carry English terms inside an otherwise Spanish-dominant query, simply because that's how the industry names them day to day.",
      },
      {
        type: "callout",
        text: "Bilingual behavior isn't a fixed language preference. It's a pattern that shifts by topic, moment, and search context.",
      },
      {
        type: "h2",
        text: "US Spanish isn't one Spanish",
      },
      {
        type: "p",
        text: "Search vocabulary varies with the predominant country of origin in a given region. A community with strong Mexican presence doesn't necessarily use the same terms as one with strong Caribbean or Central American presence. Assuming a single Spanish keyword glossary for the whole country ignores that real variation.",
      },
      {
        type: "h2",
        text: "Trust signals for the Spanish-speaking buyer",
      },
      {
        type: "list",
        items: [
          "Spanish content with the same editorial depth as the English version, not an abbreviated or lower-priority version.",
          "Customer support and checkout available in the language the search originated in.",
          "Social proof and testimonials that reflect the actual audience the content targets.",
          "Consistency between what the Spanish page promises and what can actually be resolved in that language further down the funnel.",
        ],
      },
      {
        type: "h2",
        text: "Bilingual site architecture: options",
      },
      {
        type: "table",
        head: ["Option", "When it fits", "Main risk"],
        rows: [
          [
            "Language subfolders (/es/, no prefix for English)",
            "Resource-limited sites that want to concentrate domain authority in one place",
            "Requires careful hreflang and canonicals to avoid duplicate content",
          ],
          [
            "Spanish subdomain (es.site.com)",
            "Teams managing Spanish content as a more independent unit",
            "Can fragment authority if not linked well with the main version",
          ],
          [
            "Language switcher without separate URLs",
            "Rarely recommendable for SEO",
            "Search engines can't index or differentiate versions if content changes via JavaScript with no distinct URLs",
          ],
        ],
      },
      {
        type: "h2",
        text: "On data: what not to do",
      },
      {
        type: "p",
        text: "It's tempting to cite search volume or audience size figures to justify a bilingual strategy. The problem is that volume depends on the category, region, and tool used, and it shifts constantly. Any decision about which Spanish keywords to prioritize should be based on volume and competition research done directly against your category and your market, at the time of the project — not a generic number quoted without a source. When that specific data doesn't exist yet for your case, the right move is to flag it as pending research, not fill it in with an invented figure.",
      },
      {
        type: "h2",
        text: "Localization vs. translation checklist",
      },
      {
        type: "steps",
        items: [
          "Research your category's specific English/Spanish search behavior — don't assume it.",
          "Identify which queries benefit from region-specific vocabulary within the US.",
          "Verify real volume and competition per Spanish keyword for your market, without relying on generic figures.",
          "Design EN/ES architecture with reciprocal hreflang and correct canonicals between both versions.",
          "Give the Spanish version the same editorial depth as the English version.",
          "Enable customer support and the conversion process in the language the user arrived in.",
          "Measure the cross-language journey: users who research in one language and convert in another.",
        ],
      },
      {
        type: "h2",
        text: "The core point",
      },
      {
        type: "p",
        text: "Treating Spanish as a secondary translation of the English site wastes an audience that searches, decides, and trusts differently depending on language and context. Strategy starts with understanding that behavior, not with translating text.",
      },
    ],
    cta: {
      text: "If your brand already serves or wants to serve Hispanic consumers in the US, it's worth checking whether your current strategy reflects that real bilingual behavior.",
      label: "See Hispanic Market SEO",
      href: "/en/services/hispanic-market-seo",
    },
    related: [
      {
        label: "Translating Keywords Is Not International SEO",
        href: "/en/library/translating-keywords-is-not-international-seo",
      },
      { label: "International SEO", href: "/en/services/international-seo" },
      {
        label: "Hispanic Market SEO",
        href: "/en/services/hispanic-market-seo",
      },
    ],
  },
};
